import { ErrorState } from "@/components/patterns";
import { ApplyFromUrl } from "@/lib/openWithConfig";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import type { Task, TaskType } from "@/types/task";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { TaskCompleteDialog } from "@/components/tasks/TaskCompleteDialog";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import { FilterBar } from "@/components/filters/FilterBar";
import { PageHeader, ViewsPanel, Person } from "@/components/patterns";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { useConfirm } from "@/context/ConfirmContext";
import { parseQuickTask } from "@/lib/parseQuickTask";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { cn } from "@/lib/utils";
import {
  CalendarCheck2,
  CalendarClock,
  CalendarDays,
  CheckSquare2,
  Flag,
  ListFilter,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Pencil,
  PhoneCall,
  Plus,
  Trash2,
  Undo2,
} from "lucide-react";
import { dateLocale, withClock } from "@/lib/dateTime";

interface WeekProgress {
  total: number;
  done: number;
  on_time_pct: number | null;
  team_on_time_pct: number | null;
}
interface Summary {
  overdue: number;
  today: number;
  upcoming: number;
  completed: number;
  open?: number;
  assigned_by_me?: number;
  week?: WeekProgress;
}

const TASK_FILTER_DEFAULTS = { type: "all", priority: "all" };
const TYPE_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "CALL", label: "Calls" },
  { value: "EMAIL", label: "Emails" },
  { value: "WHATSAPP", label: "WhatsApp" },
  { value: "MEETING", label: "Meetings" },
  { value: "FOLLOW_UP", label: "Follow-ups" },
  { value: "OTHER", label: "Other" },
];
const PRIORITY_OPTIONS = [
  { value: "all", label: "Any priority" },
  { value: "HIGH", label: "High" },
  { value: "NORMAL", label: "Normal" },
  { value: "LOW", label: "Low" },
];

const TYPE_STYLE: Record<TaskType, { icon: typeof PhoneCall; tone: string; label: string }> = {
  CALL: { icon: PhoneCall, tone: "bg-info-soft text-info", label: "Call" },
  EMAIL: { icon: Mail, tone: "bg-accent text-accent-foreground", label: "Email" },
  WHATSAPP: { icon: MessageSquare, tone: "bg-success-soft text-success", label: "WhatsApp" },
  MEETING: { icon: CalendarClock, tone: "bg-gold-soft text-gold-ink", label: "Meeting" },
  FOLLOW_UP: { icon: CalendarCheck2, tone: "bg-muted text-muted-foreground", label: "Follow-up" },
  OTHER: { icon: CheckSquare2, tone: "bg-muted text-muted-foreground", label: "Task" },
};
const RELATED_PATH: Record<string, string> = { LEAD: "leads", DEAL: "deals", ACCOUNT: "accounts", CONTACT: "contacts", QUOTE: "quotes" };

const startOfDay = (d = new Date()) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const todayRange = () => {
  const s = startOfDay();
  return { from: s.toISOString(), to: new Date(s.getTime() + 86399999).toISOString() };
};

/** Which heading a task sits under, by when it is due */
function groupOf(t: Task, view: string): { key: string; label: string; tone: "danger" | "primary" | "neutral" } {
  if (view === "completed") return { key: "done", label: "Completed", tone: "neutral" };
  const due = new Date(t.due_at);
  const today = startOfDay();
  const tomorrow = new Date(today.getTime() + 86400000);
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + ((7 - today.getDay()) % 7 || 7));
  endOfWeek.setHours(23, 59, 59, 999);
  if (due < today) return { key: "overdue", label: "Overdue", tone: "danger" };
  if (due < tomorrow) return { key: "today", label: "Today", tone: "primary" };
  if (due <= endOfWeek) return { key: "week", label: "Later this week", tone: "neutral" };
  return { key: "later", label: "Later", tone: "neutral" };
}

function dueText(t: Task) {
  const d = new Date(t.due_at);
  const today = startOfDay();
  const days = Math.floor((startOfDay(d).getTime() - today.getTime()) / 86400000);
  const clock = t.all_day ? "" : d.toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }));
  if (days === 0) return clock || "Today";
  if (days === -1) return "Yesterday";
  if (days < 0 && days > -7) return `${-days} days ago`;
  if (days === 1) return `Tomorrow ${clock}`.trim();
  if (days > 1 && days < 7) return `${d.toLocaleDateString(dateLocale(), { weekday: "short" })} ${clock}`.trim();
  return `${d.toLocaleDateString(dateLocale(), { day: "numeric", month: "short" })} ${clock}`.trim();
}

const initials = (u?: { first_name?: string; last_name?: string }) => `${u?.first_name || ""} ${u?.last_name || ""}`.trim();

export default function TasksPage() {
  const { user } = useAuth();
  const { success, error } = useToast();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const view = searchParams.get("view") || "mine";
  const [search, setSearch] = useState("");
  const filters = usePersistentFilters("tasks", TASK_FILTER_DEFAULTS);
  const [summary, setSummary] = useState<Summary>({ overdue: 0, today: 0, upcoming: 0, completed: 0 });
  const [teamOpen, setTeamOpen] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Task | null>(null);
  const [completing, setCompleting] = useState<Task | null>(null);
  const [quick, setQuick] = useState("");
  const [adding, setAdding] = useState(false);

  // "+ Create → New task" in the app frame links here with ?create=true
  useEffect(() => {
    if (searchParams.get("create") !== "true") return;
    setCreateOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete("create");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Team views are for anyone whose activity data scope is wider than their own records
  const roleCode = String(user?.UserRole?.RoleCode || "").toUpperCase();
  const activityScope = user?.data_scopes?.activity;
  const canSeeTeam = roleCode === "SUPER_ADMIN" || activityScope === "team" || activityScope === "all";

  const setView = (key: string) => {
    const next = new URLSearchParams(searchParams);
    key === "mine" ? next.delete("view") : next.set("view", key);
    setSearchParams(next);
  };

  const loadSummary = useCallback(async () => {
    const { from, to } = todayRange();
    const q = `from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    try {
      setSummary(unwrapObject<Summary>(await api.get(`/api/Task/Summary?${q}&scope=mine`)));
      if (canSeeTeam) setTeamOpen(unwrapObject<Summary>(await api.get(`/api/Task/Summary?${q}&scope=team`)).open ?? null);
    } catch {
      // The numbers are a convenience
    }
  }, [canSeeTeam]);
  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const list = usePagedList<Task>({
    deps: [view, search, filters.values],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const { from, to } = todayRange();
      const p = new URLSearchParams({ from, to, page: String(page), page_size: "50" });
      if (view === "team") (p.set("view", "open"), p.set("scope", "team"));
      else if (view === "assigned_by_me") (p.set("view", "assigned_by_me"), p.set("scope", "team"));
      else (p.set("view", view === "mine" ? "open" : view), p.set("scope", "mine"));
      if (filters.values.type !== "all") p.set("task_type", filters.values.type);
      if (search.trim()) p.set("search", search.trim());
      try {
        const res = unwrapPage<Task>(await api.get(`/api/Task/List?${p.toString()}`));
        // Priority has no server filter; narrowing a page of a few dozen rows is fine
        if (filters.values.priority !== "all") res.rows = res.rows.filter((t) => t.priority === filters.values.priority);
        return res;
      } catch (err: any) {
        throw err;
      }
    },
  });
  const reload = () => {
    list.reload();
    loadSummary();
  };

  const groups = useMemo(() => {
    const out: { key: string; label: string; tone: "danger" | "primary" | "neutral"; items: Task[] }[] = [];
    for (const t of list.rows) {
      const g = groupOf(t, view);
      let slot = out.find((x) => x.key === g.key);
      if (!slot) (slot = { ...g, items: [] }), out.push(slot);
      slot.items.push(t);
    }
    return out;
  }, [list.rows, view]);

  const parsed = parseQuickTask(quick);
  const addQuick = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!parsed) return;
    setAdding(true);
    try {
      await api.post("/api/Task/Insert", { title: parsed.title, task_type: parsed.task_type, priority: parsed.priority, due_at: parsed.due_at.toISOString() });
      success("Task added", `${parsed.typeLabel} · ${parsed.dueLabel}`);
      setQuick("");
      reload();
    } catch (err: any) {
      error("Could not add the task", err.message || "Try again");
    } finally {
      setAdding(false);
    }
  };

  const complete = async (t: Task) => {
    try {
      await api.patch(`/api/Task/Complete/${t.id}`, { outcome: "Done" });
      reload();
    } catch (err: any) {
      error("Could not complete the task", err.message || "Try again");
    }
  };
  const reopen = async (t: Task) => {
    try {
      await api.patch(`/api/Task/Reopen/${t.id}`);
      reload();
    } catch (err: any) {
      error("Could not reopen the task", err.message || "Try again");
    }
  };
  const snooze = async (t: Task, when: "hour" | "tomorrow" | "next_week") => {
    const until = new Date();
    if (when === "hour") until.setHours(until.getHours() + 1);
    else if (when === "tomorrow") (until.setDate(until.getDate() + 1), until.setHours(9, 0, 0, 0));
    else (until.setDate(until.getDate() + (((8 - until.getDay()) % 7) || 7)), until.setHours(9, 0, 0, 0));
    try {
      await api.patch(`/api/Task/Snooze/${t.id}`, { until: until.toISOString() });
      success("Snoozed", `Now due ${until.toLocaleString(dateLocale(), withClock({ weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }))}`);
      reload();
    } catch (err: any) {
      error("Could not snooze", err.message || "Try again");
    }
  };
  const remove = async (t: Task) => {
    if (!(await confirm({ title: `Delete "${t.title}"?`, message: "This can't be undone.", confirmLabel: "Delete", destructive: true }))) return;
    try {
      await api.delete(`/api/Task/Delete/${t.id}`);
      reload();
    } catch (err: any) {
      error("Could not delete", err.message || "Try again");
    }
  };

  const week = summary.week;
  const weekPct = week && week.total ? Math.round((week.done / week.total) * 100) : 0;
  const headline =
    view === "completed" ? `${summary.completed} completed` : (
      <>
        <span className={cn(summary.overdue > 0 && "font-semibold text-danger")}>{summary.overdue} overdue</span> · {summary.today} due today · {summary.upcoming} later
      </>
    );
  const titles: Record<string, string> = { mine: "My tasks", overdue: "Overdue", today: "Today", upcoming: "Upcoming", assigned_by_me: "Assigned by me", team: "Team tasks", completed: "Completed" };

  // A saved view, or the filters Ask the CRM hands over with ?apply=
  const applySaved = (c: any) => {
    setView(c.view || "mine");
    setSearch(c.search || "");
    filters.replace(c.filters || {});
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <ApplyFromUrl apply={applySaved} />
      <ViewsPanel
        title="Tasks"
        subtitle="Follow-ups and reminders"
        saved={{
          module: "tasks",
          getConfig: () => ({ view, search, filters: filters.values }),
          apply: applySaved,
        }}
        active={view}
        onSelect={setView}
        groups={[
          {
            items: [
              { key: "mine", label: "My tasks", count: summary.open },
              { key: "overdue", label: "Overdue", count: summary.overdue, tone: "danger" },
              { key: "today", label: "Today", count: summary.today },
              { key: "upcoming", label: "Upcoming", count: summary.upcoming },
              { key: "assigned_by_me", label: "Assigned by me", count: summary.assigned_by_me, hidden: !canSeeTeam },
              { key: "team", label: "Team tasks", count: teamOpen ?? undefined, hidden: !canSeeTeam },
              { key: "completed", label: "Completed", count: summary.completed },
            ],
          },
        ]}
        footer={
          week && (
            <div className="mt-4 flex flex-col gap-1.5 px-2.5">
              <div className="text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">This week</div>
              <div className="flex justify-between text-xs">
                <span className="text-foreground">Done</span>
                <span className="font-mono font-semibold">
                  {week.done} of {week.total}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-border" role="img" aria-label={`${weekPct}% of this week's tasks done`}>
                <div className="h-full rounded-full bg-success" style={{ width: `${weekPct}%` }} />
              </div>
              <span className="text-[11.5px] text-muted-foreground">
                {week.on_time_pct === null ? "Nothing finished yet" : `On time ${week.on_time_pct}%`}
                {week.team_on_time_pct !== null && week.team_on_time_pct !== undefined ? ` · team average ${week.team_on_time_pct}%` : ""}
              </span>
            </div>
          )
        }
      />

      <PageHeader
        title="Tasks"
        summary={list.total}
        actions={
          <>
            <Button asChild variant="outline" size="sm" className="hidden md:inline-flex">
              <Link to="/calendar">
                <CalendarDays className="mr-1.5 size-4" />
                Calendar
              </Link>
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1.5 size-4" />
              New task
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-baseline gap-2.5">
        <h2 className="text-[19px] font-semibold tracking-tight">{titles[view] || "Tasks"}</h2>
        <span className="text-[13px] text-muted-foreground">{headline}</span>
      </div>

      {/* Quick add */}
      <form onSubmit={addQuick} className="flex flex-wrap items-center gap-2.5 rounded-xl border border-primary/30 bg-accent/40 px-3.5 py-2">
        <Plus className="size-[18px] shrink-0 text-primary" aria-hidden="true" />
        <label className="sr-only" htmlFor="quick-task">
          Quick add task
        </label>
        <input
          id="quick-task"
          value={quick}
          onChange={(e) => setQuick(e.target.value)}
          placeholder="Call Meena about rayon prints tomorrow 3pm"
          className="h-8 min-w-[220px] flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        {parsed && (
          <span className="flex flex-wrap gap-1.5 text-[11.5px] font-semibold">
            <span className={cn("rounded px-1.5 leading-[22px]", TYPE_STYLE[parsed.task_type].tone)}>{parsed.typeLabel}</span>
            <span className={cn("rounded px-1.5 leading-[22px]", parsed.hasDate ? "bg-warning-soft text-warning" : "bg-muted text-muted-foreground")}>{parsed.dueLabel}</span>
            {parsed.high && <span className="rounded bg-danger-soft px-1.5 leading-[22px] text-danger">High</span>}
          </span>
        )}
        <Button type="submit" size="sm" disabled={!parsed || adding}>
          Add
        </Button>
      </form>

      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: "Search tasks" }}
        fields={[
          { name: "type", label: "Type", icon: ListFilter, type: "select", options: TYPE_OPTIONS, defaultValue: "all" },
          { name: "priority", label: "Priority", icon: Flag, type: "select", options: PRIORITY_OPTIONS, defaultValue: "all" },
        ]}
        values={filters.values}
        onChange={(n, v) => filters.set(n as keyof typeof TASK_FILTER_DEFAULTS, v)}
        onReset={filters.reset}
        shown={list.total}
        total={list.total}
        noun="tasks"
      />

      {list.loading && list.rows.length === 0 ? (
        <TableSkeleton rows={6} columns={4} />
      ) : list.error ? (
        <ErrorState title="Couldn't load your tasks" onRetry={list.reload} error={list.error} />
      ) : groups.length === 0 ? (
        <div className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed border-border py-12 text-center">
          <CheckSquare2 className="size-6 text-muted-foreground" />
          <span className="text-sm font-semibold">{view === "overdue" ? "Nothing overdue" : view === "completed" ? "Nothing completed yet" : "No tasks here"}</span>
          <span className="max-w-sm text-xs text-muted-foreground">{view === "overdue" ? "You're on top of it." : "Type one in the box above, like “Call Meena tomorrow 3pm”."}</span>
        </div>
      ) : (
        groups.map((g) => (
          <section key={g.key} className="flex flex-col gap-1.5">
            <h3 className={cn("mt-1 flex items-center gap-2 text-[11.5px] font-semibold uppercase tracking-wide", g.tone === "danger" ? "text-danger" : g.tone === "primary" ? "text-primary" : "text-muted-foreground")}>
              {g.label} <span className="font-mono text-[11px]">{g.items.length}</span>
            </h3>
            <div className={cn("overflow-hidden rounded-lg border bg-card", g.tone === "danger" ? "border-danger/30" : "border-border")}>
              {g.items.map((t, i) => {
                const style = TYPE_STYLE[t.task_type] || TYPE_STYLE.OTHER;
                const Icon = style.icon;
                const done = t.task_status === "DONE";
                const late = !done && new Date(t.due_at) < new Date();
                const owner = initials(t.assigned_to);
                const path = t.related_type && t.related_id ? `/${RELATED_PATH[t.related_type]}/${t.related_id}` : null;
                return (
                  <div key={t.id} className={cn("flex flex-wrap items-center gap-3 px-3.5 py-2", i > 0 && "border-t border-border/70")}>
                    <input
                      type="checkbox"
                      checked={done}
                      onChange={() => (done ? reopen(t) : complete(t))}
                      aria-label={done ? `Reopen: ${t.title}` : `Mark done: ${t.title}`}
                      className="size-4 shrink-0 cursor-pointer accent-success"
                    />
                    <span className={cn("flex size-[26px] shrink-0 items-center justify-center rounded-md", style.tone)}>
                      <Icon className="size-[13px]" />
                    </span>
                    <span className="flex min-w-[220px] flex-1 flex-col leading-snug">
                      <button type="button" onClick={() => setEditing(t)} className={cn("text-left text-sm font-medium hover:text-primary", done && "text-muted-foreground line-through")}>
                        {t.title}
                      </button>
                      <span className="text-[11.5px] text-muted-foreground">
                        {path ? (
                          <Link to={path} className="text-primary hover:underline">
                            {t.related_name || t.related_type}
                          </Link>
                        ) : (
                          "Not linked"
                        )}{" "}
                        · {style.label}
                      </span>
                    </span>
                    {t.priority === "HIGH" && <span className="rounded bg-danger-soft px-1.5 text-[11px] font-semibold leading-5 text-danger">High</span>}
                    <span className={cn("min-w-[92px] text-right font-mono text-xs font-semibold", late ? "text-danger" : g.key === "today" ? "text-primary" : "text-foreground")}>{done && t.completed_at ? new Date(t.completed_at).toLocaleDateString(dateLocale(), { day: "numeric", month: "short" }) : dueText(t)}</span>
                    {(view === "team" || view === "assigned_by_me") && owner ? <Person name={owner} className="text-xs" /> : null}
                    <div className="flex items-center gap-1">
                      {!done && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="h-[26px] px-2 text-[11.5px]">
                              Snooze
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => snooze(t, "hour")}>In 1 hour</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => snooze(t, "tomorrow")}>Tomorrow 9:00</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => snooze(t, "next_week")}>Next Monday 9:00</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm" aria-label={`More for ${t.title}`}>
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditing(t)}>
                            <Pencil className="mr-2 size-3.5" /> Edit
                          </DropdownMenuItem>
                          {!done && (
                            <DropdownMenuItem onClick={() => setCompleting(t)}>
                              <CheckSquare2 className="mr-2 size-3.5" /> Complete with a note
                            </DropdownMenuItem>
                          )}
                          {done && (
                            <DropdownMenuItem onClick={() => reopen(t)}>
                              <Undo2 className="mr-2 size-3.5" /> Reopen
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => remove(t)} className="text-danger focus:text-danger">
                            <Trash2 className="mr-2 size-3.5" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
      <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={list.loading} />

      <TaskDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={reload} />
      <TaskDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} task={editing} onSaved={reload} />
      <TaskCompleteDialog open={!!completing} onOpenChange={(o) => !o && setCompleting(null)} task={completing} onCompleted={reload} />
    </div>
  );
}
